// src/app/core/shared/city.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';

export type Tier = 't0' | 't3' | 't6';
export type Availability = 'available' | 'unavailable';

export interface CityApiItem {
  city_id: number;
  city_name: string;
  city_specification: 'city' | 'country' | string;
  city_t0_status: Availability | string;
  city_t3_status: Availability | string;
  city_t6_status: Availability | string;
  country_name: string;
}

export interface CityApiResponse {
  status: 'success' | 'fail';
  count?: number;
  cities?: CityApiItem[];
  message?: string;
  all_access?: boolean;
  user_mapping?: any;
}

export interface CityOption {
  id: number;
  country: string;
  city: string;
  specification: 'city' | 'country' | string;
  availability: Record<Tier, boolean>;
  raw: CityApiItem;
}

const LOCAL_CITY_JSON_PATH = 'assets/json-data/city_service.json';

@Injectable({ providedIn: 'root' })
export class CityService {
  private readonly cityUrl: string;
  private readonly allCities$: Observable<CityOption[]>;

  constructor(private http: HttpClient) {
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    this.cityUrl = isLocalhost ? '/city' : `${environment.apiUrl}/city`;

    const sessionId = this.ensureSessionId();
    const email = this.readUserEmail();

    let params = new HttpParams().set('session_id', sessionId);
    if (email) {
      params = params.set('email', email);
    }

    this.allCities$ = (
      isLocalhost
        ? this.http.get<CityApiResponse>(LOCAL_CITY_JSON_PATH)
        : this.http.get<CityApiResponse>(this.cityUrl, { params })
    ).pipe(
      map((res) => {
        if (res?.status !== 'success' || !Array.isArray(res.cities)) {
          return [];
        }

        return res.cities.map((c) => this.toOption(c));
      }),
      shareReplay(1)
    );
  }

  getAll(): Observable<CityOption[]> {
    return this.allCities$;
  }

  getAvailableForTier(tier: Tier): Observable<CityOption[]> {
    return this.getAll().pipe(map((list) => list.filter((x) => x.availability[tier])));
  }

  private ensureSessionId(): string {
    const key = 'session_id';
    let id = sessionStorage.getItem(key) || '';
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        sessionStorage.setItem(key, id);
      } catch { }
    }
    return id;
  }

  private readUserEmail(): string {
    return (
      sessionStorage.getItem('userEmail') ||
      sessionStorage.getItem('userEmailFromMe') ||
      ''
    ).trim().toLowerCase();
  }

  private toOption(c: CityApiItem): CityOption {
    const norm = (s: any) => String(s ?? '').trim().toLowerCase();
    const isAvail = (s: any) => norm(s) === 'available';

    return {
      id: c.city_id,
      country: c.country_name,
      city: c.city_name,
      specification: c.city_specification,
      availability: {
        t0: isAvail(c.city_t0_status),
        t3: isAvail(c.city_t3_status),
        t6: isAvail(c.city_t6_status)
      },
      raw: c
    };
  }
}